/**
 * Balance card for /billing/family-balance (Plan 09).
 *
 * Layout (mirrors mockup-9 screenshot 2.41.55-am):
 *
 *   ┌────────────────────────────────────────────────────────┐
 *   │ Your balance                          [ Top up ▼ ]     │
 *   │ Numan Household                                        │
 *   │ St. Hugo of the Hills — Avery, Isaac, Lincoln          │
 *   │ $300,000.00                                            │
 *   ├────────────────────────────────────────────────────────┤
 *   │ Auto-reload     ───────────────────────────  [● On ]  │
 *   │   Re-load $25 below $10 or when an order is short     │
 *   └────────────────────────────────────────────────────────┘
 *
 * The household identity under the header label is what lets a parent on
 * more than one household tell her wallets apart before she funds one
 * (PROD-1313) — the NAME alone can't, since it derives from adult surnames
 * and collides across a parent's households by construction, so the school +
 * students carry it.
 *
 * Header label switches to "Household balance" when the parent has
 * opted into shared mode. Today we always label "Your balance" — the
 * shared-mode toggle is hidden in v1 per the release-coupling rule
 * with Plan 10 (Account Co-Ownership).
 */

import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader } from '@ordo/ui/card'
import { toast } from '@ordo/ui/sonner'
import { Switch } from '@ordo/ui/switch'
import { AlertCircle, Loader2, Plus } from 'lucide-react'
import { useState } from 'react'

import { type BillingFamilyAccount, useSetAutoReloadMutation } from '@/api/billing-family'
import { useFamilySubscriptionsQuery } from '@/api/family-subscriptions'
import { autoReloadRowConsequence, selectAutoReloadAlert } from '@/lib/auto-reload-alert'
import { supportMailto } from '@/lib/support'
import { isPendingClearance, PENDING_CLEARANCE_HINT } from '@/lib/top-up-polling'

import { AutoReloadDialog } from './auto-reload-dialog'
import { DEFAULT_AUTO_RELOAD_THRESHOLD_CENTS, formatCents, householdCoverageLine } from './helpers'

export interface BalanceCardProps {
  account: BillingFamilyAccount
  /** When true, header reads "Household balance" instead of "Your balance". */
  sharedMode?: boolean
  /**
   * Read-only view for a shared household the viewer doesn't own — hides
   * Top up and the auto-reload editor (only the owner manages funding).
   */
  readOnly?: boolean
  /**
   * A just-submitted top-up is still being credited (webhook in flight). Shows
   * a live "Updating…" note under the balance so the displayed number never
   * looks silently stale.
   */
  pending?: boolean
  /** The pending credit has outrun the poll window — show a gentle note. */
  stillProcessing?: boolean
  onTopUp: () => void
  onRefetch: () => void
}

export function BalanceCard({
  account,
  sharedMode,
  readOnly,
  pending,
  stillProcessing,
  onTopUp,
  onRefetch,
}: BalanceCardProps) {
  const [autoReloadOpen, setAutoReloadOpen] = useState(false)
  const fundingEnabled = account.fundingEnabled !== false
  const disableReload = useSetAutoReloadMutation()
  const subscriptionsQuery = useFamilySubscriptionsQuery()
  const headerLabel = sharedMode ? 'Household balance' : 'Your balance'
  // WHICH wallet this is. Two of a parent's households can carry the same
  // display name, so the coverage line is what actually tells them apart.
  const householdName = account.displayName?.trim() || null
  const coverage = householdCoverageLine(account)
  const balanceText = formatCents(account.balanceCents, account.currency)
  // Clawbacks (card refunds / disputes) can push the wallet below zero;
  // ordering self-blocks until it's topped back up — say so.
  const negative = account.balanceCents < 0
  // A bank (ACH) top-up is spendable the moment Stripe accepts the debit, but
  // the transfer itself takes days — say so, or the balance looks unexplained.
  const clearing = account.ledger.edges.some((e) => isPendingClearance(e.node))
  const autoReloadLabel = account.autoReload
    ? `Re-load ${formatCents(account.autoReloadAmountCents, account.currency)} when balance drops below ${formatCents(
        account.autoReloadThresholdCents ?? DEFAULT_AUTO_RELOAD_THRESHOLD_CENTS,
        account.currency,
      )} or can't cover an order`
    : 'Off — your balance will not auto-reload'
  // The row used to stop at "will not auto-reload" and never mention that
  // auto-orders are charged to this balance and stop running without it. The
  // condition is server-derived (`GET /api/family-subscriptions`), so this
  // line can never disagree with the Home alert or the My Family badges.
  const autoReloadConsequence = autoReloadRowConsequence(
    selectAutoReloadAlert(subscriptionsQuery.data?.autoReloadAlerts, account.id),
  )

  return (
    <>
      <Card>
        <CardHeader className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-col gap-1.5">
            <div className="flex min-w-0 flex-col gap-0.5">
              <p className="text-sm text-muted-foreground">{headerLabel}</p>
              {householdName ? <p className="text-sm font-semibold text-foreground">{householdName}</p> : null}
              {coverage ? <p className="text-xs text-muted-foreground">{coverage}</p> : null}
            </div>
            <h2 className={`text-4xl font-semibold tracking-tight ${negative ? 'text-destructive' : ''}`}>
              {balanceText}
            </h2>
            {negative && (
              <p className="text-xs text-muted-foreground">
                {fundingEnabled
                  ? 'Your balance is negative after a refund or dispute. Add funds to keep ordering.'
                  : 'Your household has an outstanding balance. Contact support to resolve it.'}
              </p>
            )}
            {clearing && <p className="text-xs text-muted-foreground">{PENDING_CLEARANCE_HINT}</p>}
            {pending ? (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground" role="status" aria-live="polite">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                {stillProcessing ? 'Still adding your funds — this can take a minute.' : 'Updating your balance…'}
              </p>
            ) : null}
          </div>
          {!readOnly && fundingEnabled && (
            <div className="flex flex-shrink-0 items-center gap-2">
              <Button variant="default" onClick={onTopUp}>
                <Plus className="mr-1.5 h-4 w-4" aria-hidden />
                Add funds
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="border-t [&:not(:first-child)]:pt-6">
          {!fundingEnabled ? (
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>Your school manages meal ordering. Your existing balance and billing history remain available here.</p>
              <a
                className="font-medium text-foreground underline"
                href={supportMailto('School-managed meals: household billing')}>
                {account.balanceCents !== 0
                  ? 'Contact support about your balance'
                  : 'Contact support about your billing'}
              </a>
              {account.autoReload && !readOnly ? (
                <Button
                  variant="outline"
                  disabled={disableReload.isPending}
                  onClick={async () => {
                    try {
                      await disableReload.mutateAsync({
                        accountId: account.id,
                        enabled: false,
                        pauseAffectedSubscriptions: true,
                      })
                      toast.success('Auto-reload turned off.')
                      onRefetch()
                    } catch (error) {
                      toast.error(error instanceof Error ? error.message : 'Could not turn off auto-reload')
                    }
                  }}>
                  Turn off auto-reload
                </Button>
              ) : null}
            </div>
          ) : readOnly ? (
            <p className="text-xs text-muted-foreground">
              {account.canCharge
                ? 'Shared with you by the household owner — adding funds, auto-reload, and payment methods are managed by them.'
                : "You can view this household's balance, but you can't spend it until the owner turns on balance sharing."}
            </p>
          ) : (
            <div className="flex items-center justify-between gap-4">
              <div className="flex min-w-0 flex-col gap-1">
                <button
                  type="button"
                  onClick={() => setAutoReloadOpen(true)}
                  className="text-left text-sm font-medium text-foreground hover:underline">
                  Auto-reload
                </button>
                <p className="text-xs text-muted-foreground">{autoReloadLabel}</p>
                {autoReloadConsequence ? (
                  <p className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    {autoReloadConsequence}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setAutoReloadOpen(true)}
                aria-label="Open auto-reload settings"
                className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">{account.autoReload ? 'On' : 'Off'}</span>
                <Switch
                  checked={account.autoReload}
                  onClick={(e) => {
                    // Don't toggle inline — open the sheet so the user
                    // can confirm threshold + amount + (later) the
                    // payment-method picker.
                    e.preventDefault()
                    setAutoReloadOpen(true)
                  }}
                />
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {fundingEnabled ? (
        <AutoReloadDialog
          open={autoReloadOpen}
          onClose={() => setAutoReloadOpen(false)}
          account={account}
          onSaved={() => {
            setAutoReloadOpen(false)
            onRefetch()
          }}
        />
      ) : null}
    </>
  )
}
